import type {
	AnyAction,
	ListenerEffectAPI,
	ThunkDispatch,
} from '@reduxjs/toolkit';

import { chatEntity } from '~entities/chats/chatEntity';
import type { TMessage } from '~entities/messages';
import { botsApi, processLastMessageText } from '~services/api/bots';
import { isSafeApp } from '~shared/utils/host';

import type { AppDispatch, RootState } from '..';
import { sortChats } from './sortChats';

export const updateChat = async (
	listenerApi: ListenerEffectAPI<
		unknown,
		ThunkDispatch<unknown, unknown, AnyAction>
	>,
	message: TMessage
) => {
	const { id: userId } = (listenerApi.getState() as RootState).auth.info;
	const { id, text, meta, recipient, sender, timestamp } = message;
	const isOwn = sender === userId;
	const botId = isOwn ? recipient : sender;
	const lastMessage = processLastMessageText(text, meta);
	const isSexting = meta?.ranking?.labels?.includes('sexting') || false;
	const notOwnSexting = isSexting && !isOwn;
	let { data: chat } = await listenerApi.dispatch(
		botsApi.endpoints.getChat.initiate({
			userId,
			recipientId: botId,
		})
	);

	if (chat && (chat.bot.visibility !== 'default' || !isSafeApp)) {
		chat =
			chat.lastMessage?.id === id
				? {
						...chat,
						isHidden: false,
				  }
				: {
						...chat,
						counters: {
							...chat.counters,
							totalCount: chat.counters.totalCount + 1,
							unreadCount: chat.counters.unreadCount + (isOwn ? 0 : 1),
						},
						lastMessage: {
							id,
							timestamp,
							senderId: sender,
							recipientId: recipient,
							isSexting:
								notOwnSexting && !/(photo|audio|video)/.test(lastMessage),
							text: lastMessage,
							tarifficationType: meta?.tariffication?.type,
							paid: meta?.tariffication?.paid ?? false,
							cost: meta?.tariffication?.cost ?? 0,
						},
						lastEventAt: chat.pinned
							? chat.lastEventAt
							: new Date(timestamp).toISOString(),
						isHidden: false,
				  };

		chatEntity.updateCollection<{ search: string }>(
			listenerApi.getState,
			listenerApi.dispatch,
			(chats, args) => {
				const index = chats.findIndex((chat) => chat.botId === botId);

				if (index === -1) {
					if (!args?.search) {
						chats.push(chat!);
					}
				} else {
					chats[index] = chat!;
				}

				sortChats(chats);
			}
		);

		if (!isOwn) {
			listenerApi.dispatch(
				botsApi.util.updateQueryData('getChatsCount', { userId }, (draft) => {
					draft.unreadMessagesCount++;
				}) as AppDispatch
			);
		}
	}
};
